/**
 * Who RocketRep lets in, and where each of them belongs.
 *
 * ★ THE RULING THIS FILE ENCODES (Edward, 2026-09-16):
 *   RocketRep keeps its OWN logins. It does not reuse LaaS logins, it does not
 *   proxy a sign-in to LaaS, and it does not authorise by introspecting a LaaS
 *   token. An earlier build did all three; it was reversed and deleted.
 *
 *   The reason is operational, not theoretical. A field app whose login depends
 *   on the money platform being up cannot be used in a clinic corridor while
 *   that platform is being deployed — and LaaS is deployed often. Distinct
 *   credentials break that coupling. The secondary benefit is blast radius:
 *   LaaS signs HS256, so the verifying key IS the minting key, and a sales tool
 *   that could verify a LaaS token could also mint itself a SUPERADMIN one.
 *
 *   `laasTeamMemberId` below is the one thread back to LaaS, and it is a
 *   REFERENCE — it lets a rep's visits be cross-referenced with LaaS attribution
 *   later. It is never an authentication path. Nothing may log a person in
 *   because that field is set.
 *
 * Roles are from docs/03-ORG-AND-ASSIGNMENT.md, plus SUPERADMIN for us.
 */

/* ── people ─────────────────────────────────────────────────────────────── */

/**
 * PRO      a medical representative, ~25 of them, in the field with a phone.
 * LEADER   a team leader. Has reps under them AND still works doors, so they
 *          are the one role admitted to both halves of the product.
 * MANAGER  regional / national management. Desk only.
 * SUPERADMIN  Buildfor.Healthcare. Desk only.
 */
export type Role = "PRO" | "LEADER" | "MANAGER" | "SUPERADMIN";

export const ROLES: readonly Role[] = ["PRO", "LEADER", "MANAGER", "SUPERADMIN"];

export function isRole(v: unknown): v is Role {
  return typeof v === "string" && (ROLES as readonly string[]).includes(v);
}

/** How a role is named on screen. Never show the enum to a human. */
export const ROLE_LABEL: Record<Role, string> = {
  PRO: "Medical representative",
  LEADER: "Team leader",
  MANAGER: "Regional manager",
  SUPERADMIN: "Superadmin",
};

/**
 * The signed-in person, as the browser holds them.
 *
 * ★ There is no token on this object and there never will be. The session is an
 *   httpOnly cookie: the server sets it, the browser sends it, and this code
 *   cannot read it. Anything here that looked like a credential would be a
 *   credential sitting in JavaScript's reach.
 *
 * Optional fields are optional because the server half is being written in
 * parallel and may not send them on day one. Everything that renders them
 * degrades to something honest rather than to an empty box.
 */
export interface Person {
  id: string;
  name: string;
  email: string;
  role: Role;
  /** Two letters for the avatar chip. Derived from `name` when absent. */
  initials?: string | null;
  /** A human job title — "Head of Sales" — shown in place of the role label. */
  title?: string | null;
  /** "Ghatkopar · Vikhroli · Bhandup". Assigned, never derived from a pincode. */
  territory?: string | null;
  /** The Aspira centre this person starts and closes the day at. */
  homeCentre?: { code: string; name: string } | null;
  /** A cross-reference into LaaS for attribution. NEVER an authentication path. */
  laasTeamMemberId?: number | null;
  /** Set by the server when this password was issued and must be replaced. */
  mustChangePassword?: boolean;
  active?: boolean;

  /**
   * ★ The halves the SERVER says this person may open, when it sends them.
   *
   * The service computes this itself (`src/auth/roles.ts`) and it is what
   * actually gates the data routes. Two independent tables of the same truth is
   * how a browser ends up showing a screen the API will refuse — so when the
   * server states it, its answer wins, and the role table below is the fallback
   * for a payload that does not. An EMPTY array is a real answer ("none"), not
   * a missing one, so it must be distinguished from `null`.
   */
  audiences?: Audience[] | null;
  /** Where the server would land them. Same rule: its answer wins when present. */
  landing?: string | null;
}

/* ── audiences ──────────────────────────────────────────────────────────── */

/** The two halves of the product on this one origin. */
export type Audience = "pro" | "mis";

/**
 * Who each half admits.
 *
 * LEADER appears in both deliberately: a team leader runs reps from the MIS and
 * still walks doors with the PRO app. A PRO is field-only, and a MANAGER or
 * SUPERADMIN has no book of doctors to visit.
 */
export const AUDIENCE_ROLES: Record<Audience, readonly Role[]> = {
  pro: ["PRO", "LEADER"],
  mis: ["LEADER", "MANAGER", "SUPERADMIN"],
};

export const AUDIENCE_NAME: Record<Audience, string> = {
  pro: "the field app",
  mis: "the MIS",
};

/**
 * The same names at the start of a sentence. Written out rather than produced
 * by capitalising the first letter: "the MIS" would survive that, but the rule
 * is wrong the moment a name starts with an acronym or a lowercase product
 * word, and a heading reading "the MIS is not yours to open" is exactly the
 * small wrongness that makes a screen look unfinished.
 */
export const AUDIENCE_TITLE: Record<Audience, string> = {
  pro: "The field app",
  mis: "The MIS",
};

export const AUDIENCE_PATH: Record<Audience, string> = {
  pro: "/pro",
  mis: "/",
};

export function admits(audience: Audience, role: Role): boolean {
  return AUDIENCE_ROLES[audience].includes(role);
}

/** Every half a ROLE may open. The fallback when the server states nothing. */
export function audiencesFor(role: Role): Audience[] {
  return (["pro", "mis"] as Audience[]).filter((a) => admits(a, role));
}

/** Every half THIS PERSON may open — the server's own list when it sent one. */
export function audiencesOf(person: Person): Audience[] {
  return Array.isArray(person.audiences) ? person.audiences : audiencesFor(person.role);
}

/** The guard's question. Always ask it of a person, never of a bare role. */
export function personAdmits(person: Person, audience: Audience): boolean {
  return audiencesOf(person).includes(audience);
}

/**
 * The role's own landing, mirroring `landingFor` in src/auth/roles.ts. Only the
 * fallback for a payload with no `landing` (an older server) — when the server
 * states one, its answer wins.
 *   PRO, LEADER → /pro (the field app) · MANAGER, SUPERADMIN → /cockpit
 */
export const ROLE_LANDING: Record<Role, string> = {
  PRO: "/pro",
  LEADER: "/pro",
  MANAGER: "/cockpit",
  SUPERADMIN: "/cockpit",
};

/** A same-site path, never a full or protocol-relative URL. */
function sameSite(path: string | null | undefined): path is string {
  return typeof path === "string" && path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
}

/**
 * Which half of the product a same-site path opens, or null for a path that is
 * no destination at all (the two credential screens). Mirrors App.tsx: `/pro/*`
 * is the field app and every other path is the MIS.
 */
export function audienceOfPath(path: string): Audience | null {
  const bare = path.split(/[?#]/)[0] ?? "";
  if (bare === "/login" || bare === "/change-password") return null;
  if (bare === "/pro" || bare.startsWith("/pro/")) return "pro";
  return "mis";
}

/** May this person open that path? Asked of the PERSON, as the guards ask it. */
function mayOpen(person: Person, path: string): boolean {
  const a = audienceOfPath(path);
  return a !== null && personAdmits(person, a);
}

/**
 * Where to land someone who has just signed in and asked for nothing specific.
 * The server's `landing` when it sent one AND it is a path this person may open;
 * otherwise the role's own landing; otherwise whichever half they may open.
 * A PRO is never sent to an MIS path, whatever arrived over the wire.
 */
export function homeFor(person: Person): string {
  const stated = (person.landing ?? "").trim();
  // Only a same-site path is ever followed — this arrives over the wire.
  if (sameSite(stated) && mayOpen(person, stated)) return stated;

  const own = ROLE_LANDING[person.role];
  if (own && mayOpen(person, own)) return own;

  const open = audiencesOf(person);
  if (open.includes("mis")) return AUDIENCE_PATH.mis;
  if (open.includes("pro")) return AUDIENCE_PATH.pro;
  return AUDIENCE_PATH.mis;
}

/**
 * Where to go after a sign-in, given where they were heading when they were
 * bounced to /login (`from`, from router state).
 *
 * `from` wins when it is a same-site path this person may open — a deep link to
 * a doctor survives the trip. It loses when:
 *   · it is not same-site, or is one of the credential screens;
 *   · it is a half this person may not open (a PRO holding an MIS link goes to
 *     /pro, not to a refusal screen);
 *   · it is the bare root "/". That is what a browser asks for when somebody
 *     types the address — "nothing specific" — so it takes the landing. Home is
 *     still one click away in the menu, and "/" itself is never redirected.
 */
export function landingAfterSignIn(person: Person, from: string | null | undefined): string {
  if (sameSite(from)) {
    const bare = from.split(/[?#]/)[0] ?? "";
    if (bare !== "/" && bare !== "" && mayOpen(person, from)) return from;
  }
  return homeFor(person);
}

/** The path that opens `audience` for this person: their landing when it is in
 *  that half, else the half's root. For the "open the other half" buttons. */
export function pathForAudience(person: Person, audience: Audience): string {
  const home = homeFor(person);
  if (audienceOfPath(home) === audience) return home;
  // A LEADER's bare "/" goes to his field app, so his way into the MIS is his team.
  if (audience === "mis" && person.role === "LEADER") return "/team";
  return AUDIENCE_PATH[audience];
}

/* ── display helpers ────────────────────────────────────────────────────── */

/** "Sameer Kulkarni" → "SK". Falls back to the email, then to a dash. */
export function initialsOf(person: Pick<Person, "name" | "email" | "initials">): string {
  const given = (person.initials ?? "").trim();
  if (given) return given.slice(0, 2).toUpperCase();

  const parts = (person.name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[parts.length - 1]?.[0] ?? "";
    const pair = `${a}${b}`.toUpperCase();
    if (pair.length === 2) return pair;
  }
  const one = parts[0] ?? "";
  if (one.length >= 2) return one.slice(0, 2).toUpperCase();

  const local = (person.email ?? "").split("@")[0] ?? "";
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return "—";
}

/** What to print under the name: a real job title if we have one, else the role. */
export function titleOf(person: Person): string {
  const t = (person.title ?? "").trim();
  return t || ROLE_LABEL[person.role];
}

/** The greeting name. "Sameer Kulkarni" → "Sameer". */
export function firstNameOf(person: Person): string {
  const first = (person.name ?? "").trim().split(/\s+/)[0];
  return first || (person.email ?? "").split("@")[0] || "there";
}
